import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { ScanLine, Camera, Undo2, CheckCircle2, Loader2, Volume2, Plus, X } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { ProductThumb } from "@/components/common/ProductThumb";
import { CameraBarcodeScanner } from "@/components/common/CameraBarcodeScanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MODES = ["receipt", "pick", "return", "transfer", "count", "adjust"];
const MODE_PERM = { receipt: "inventory:write", return: "inventory:write", transfer: "inventory:write", count: "inventory:adjust", adjust: "inventory:adjust", pick: "deliveries:write" };
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination); o.frequency.value = 880; g.gain.value = 0.05;
    o.start(); setTimeout(() => { o.stop(); ctx.close(); }, 90);
  } catch { /* ignore */ }
}

export default function ScanCenter() {
  const { t } = useI18n();
  const { can } = useAuth();
  const availableModes = MODES.filter((m) => can(MODE_PERM[m]));
  const [mode, setMode] = useState(availableModes[0] || "receipt");
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [warehouseToId, setWarehouseToId] = useState("");
  const [reason, setReason] = useState("");
  const [session, setSession] = useState(null);
  const [scanValue, setScanValue] = useState("");
  const [qty, setQty] = useState("1");
  const [busy, setBusy] = useState(false);
  const [posted, setPosted] = useState(false);
  const [sound, setSound] = useState(() => localStorage.getItem("mayr_scan_sound") !== "0");
  const [vibrate, setVibrate] = useState(() => localStorage.getItem("mayr_scan_vibrate") !== "0");
  const [ambiguous, setAmbiguous] = useState(null);
  const [camera, setCamera] = useState(false);
  const scanRef = useRef(null);
  const lastScan = useRef({ code: "", ts: 0 });
  const cameraSupported = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

  useEffect(() => { localStorage.setItem("mayr_scan_sound", sound ? "1" : "0"); }, [sound]);
  useEffect(() => { localStorage.setItem("mayr_scan_vibrate", vibrate ? "1" : "0"); }, [vibrate]);

  useEffect(() => {
    api.get("/inventory/warehouses").then(({ data }) => {
      setWarehouses(data);
      const def = data.find((w) => w.is_default) || data[0];
      if (def) { setWarehouseId(def.id); setWarehouseToId(data.find((w) => w.id !== def.id)?.id || ""); }
    });
  }, []);

  const feedback = useCallback((ok) => {
    if (sound) beep();
    if (vibrate && navigator.vibrate) navigator.vibrate(ok ? 40 : [60, 40, 60]);
  }, [sound, vibrate]);

  const startSession = async () => {
    setBusy(true);
    try {
      const body = { mode, warehouse_id: warehouseId, note: reason || undefined };
      if (mode === "transfer") body.warehouse_to_id = warehouseToId;
      const { data } = await api.post("/scan/sessions", body);
      setSession(data); setPosted(false);
      setTimeout(() => scanRef.current?.focus(), 50);
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
    finally { setBusy(false); }
  };

  const refresh = async (id) => { const { data } = await api.get(`/scan/sessions/${id}`); setSession(data); };

  const submitScan = async (rawCode) => {
    const code = (rawCode ?? scanValue).trim();
    if (!code || !session) return;
    const now = Date.now();
    if (lastScan.current.code === code && now - lastScan.current.ts < 900) { setScanValue(""); return; } // debounce
    lastScan.current = { code, ts: now };
    setScanValue("");
    try {
      const { data } = await api.post(`/scan/sessions/${session.id}/lines`, {
        client_line_id: uid(), code, quantity: qty || "1",
      });
      if (data.resolve && data.resolve.status === "not_found") { feedback(false); toast.error(t("scan.not_found") + `: ${code}`); return; }
      if (data.resolve && data.resolve.status === "ambiguous") { feedback(false); setAmbiguous(data.resolve); return; }
      if (data.duplicate) { toast(t("scan.duplicate_ignored")); return; }
      feedback(true);
      await refresh(session.id);
    } catch (e) { feedback(false); toast.error(apiErrorMessage(e, t)); }
    finally { scanRef.current?.focus(); }
  };

  const chooseAmbiguous = async (productId) => {
    setAmbiguous(null);
    try {
      await api.post(`/scan/sessions/${session.id}/lines`, { client_line_id: uid(), product_id: productId, quantity: qty || "1" });
      feedback(true); await refresh(session.id);
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
    finally { scanRef.current?.focus(); }
  };

  const removeLine = async (lineId) => {
    await api.delete(`/scan/sessions/${session.id}/lines/${lineId}`);
    await refresh(session.id);
  };

  const doPost = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/scan/sessions/${session.id}/post`, { idempotency_key: session.id });
      setPosted(true);
      toast.success(t("scan.post_success", { n: data.movements ?? 0 }));
      await refresh(session.id);
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
    finally { setBusy(false); }
  };

  const lines = session?.lines || [];

  return (
    <div className="mx-auto max-w-2xl pb-24" data-testid="scan-center">
      <PageHeader title={t("scan.title")} />
      <p className="mb-4 text-sm text-slate-500">{t("scan.subtitle")}</p>

      {availableModes.length === 0 && (
        <Card className="p-8 text-center text-sm text-slate-500" data-testid="scan-no-permission">
          {t("errors.forbidden")}
        </Card>
      )}

      {availableModes.length > 0 && !session && (
        <Card className="space-y-4 p-5" data-testid="scan-setup">
          <div className="space-y-1.5">
            <Label>{t("scan.mode")}</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="scan-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {availableModes.map((m) => <SelectItem key={m} value={m} data-testid={`scan-mode-${m}`}>{t(`scan.mode_${m}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("scan.warehouse")}</Label>
              <Select value={warehouseId} onValueChange={setWarehouseId}>
                <SelectTrigger data-testid="scan-warehouse-select"><SelectValue /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {mode === "transfer" && (
              <div className="space-y-1.5">
                <Label>{t("scan.destination")}</Label>
                <Select value={warehouseToId} onValueChange={setWarehouseToId}>
                  <SelectTrigger data-testid="scan-warehouse-to-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{warehouses.filter((w) => w.id !== warehouseId).map((w) => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          {(mode === "adjust") && (
            <div className="space-y-1.5">
              <Label>{t("scan.reason")} *</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="scan-reason-input" />
            </div>
          )}
          <Button className="h-11 w-full" disabled={busy || !warehouseId || (mode === "adjust" && !reason)} onClick={startSession} data-testid="scan-start-button">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ScanLine className="mr-2 h-4 w-4" />}{t("scan.start")}
          </Button>
        </Card>
      )}

      {session && (
        <>
          <div className="sticky top-2 z-10 mb-3 flex items-center justify-between rounded-md bg-slate-900 px-4 py-2 text-white dark:bg-white dark:text-slate-900" data-testid="scan-mode-banner">
            <span className="flex items-center gap-2 font-semibold"><ScanLine className="h-4 w-4" />{t(`scan.mode_${session.mode}`)}</span>
            <span className="text-xs opacity-80">{warehouses.find((w) => w.id === session.warehouse_id)?.code}{session.warehouse_to_id ? ` → ${warehouses.find((w) => w.id === session.warehouse_to_id)?.code}` : ""}</span>
          </div>

          {!posted && (
            <Card className="space-y-3 p-4">
              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1.5">
                  <Label>{t("scan.scan_here")}</Label>
                  <Input ref={scanRef} autoFocus value={scanValue} inputMode="none"
                    onChange={(e) => setScanValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitScan(); } }}
                    className="h-11 text-lg tabular-nums" data-testid="scan-input" />
                </div>
                <div className="w-20 space-y-1.5">
                  <Label>{t("scan.quantity")}</Label>
                  <Input type="number" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)} className="h-11 tabular-nums" data-testid="scan-qty-input" />
                </div>
                <Button variant="outline" className="h-11" onClick={() => submitScan()} data-testid="scan-add-button">{t("scan.manual_add")}</Button>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                {cameraSupported ? (
                  <Button variant="ghost" size="sm" onClick={() => setCamera(true)} data-testid="scan-camera-toggle">
                    <Camera className="h-4 w-4" />{t("scan.use_camera")}
                  </Button>
                ) : <span className="text-xs text-slate-400" data-testid="scan-camera-unsupported">{t("scan.camera_unsupported")}</span>}
                <label className="flex items-center gap-2"><Switch checked={sound} onCheckedChange={setSound} data-testid="scan-sound-toggle" /><Volume2 className="h-4 w-4" />{t("scan.sound")}</label>
                <label className="flex items-center gap-2"><Switch checked={vibrate} onCheckedChange={setVibrate} data-testid="scan-vibrate-toggle" />{t("scan.vibrate")}</label>
              </div>
            </Card>
          )}

          <Card className="mt-3 p-4">
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">{t("scan.lines")} ({lines.length})</h3>
            {lines.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">{t("scan.no_lines")}</p> : (
              <div className="space-y-2" data-testid="scan-lines">
                {lines.map((l) => (
                  <div key={l.id} className="flex items-center gap-3 rounded-md border border-slate-200 p-2 dark:border-slate-800" data-testid="scan-line">
                    <ProductThumb url={l.product?.image_url} alt={l.product?.name} className="h-10 w-10 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{l.product?.name}</div>
                      <div className="truncate text-xs text-slate-400">{l.product?.sku}{l.product?.ean_gtin ? ` · ${l.product.ean_gtin}` : ""}</div>
                      <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-slate-500">
                        <span>{t("scan.on_hand")}: <b className="tabular-nums">{l.on_hand}</b></span>
                        <span>{t("scan.available")}: <b className="tabular-nums">{l.available}</b></span>
                        {session.mode === "count" && <span>{t("scan.expected")}: <b className="tabular-nums">{l.expected_qty}</b></span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold tabular-nums" data-testid="scan-line-qty">{l.quantity}</div>
                      {session.mode === "count" && l.expected_qty != null && (
                        <div className={`text-xs tabular-nums ${Number(l.quantity) - Number(l.expected_qty) < 0 ? "text-red-600" : "text-emerald-600"}`}>Δ {(Number(l.quantity) - Number(l.expected_qty)).toFixed(2)}</div>
                      )}
                    </div>
                    {!posted && <Button variant="ghost" size="icon" className="h-11 w-11 shrink-0" onClick={() => removeLine(l.id)} data-testid="scan-line-remove"><Undo2 className="h-4 w-4 text-red-500" /></Button>}
                  </div>
                ))}
              </div>
            )}
          </Card>

          <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/95 p-3 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95">
            <div className="mx-auto flex max-w-2xl gap-2">
              {!posted ? (
                <>
                  <Button variant="outline" className="h-11" onClick={() => { api.post(`/scan/sessions/${session.id}/cancel`); setSession(null); }} data-testid="scan-cancel-button">{t("scan.cancel_session")}</Button>
                  <Button className="h-11 flex-1" disabled={busy || lines.length === 0} onClick={doPost} data-testid="scan-post-button">
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}{t("scan.post")} ({lines.length})
                  </Button>
                </>
              ) : (
                <Button className="h-11 flex-1" onClick={() => setSession(null)} data-testid="scan-new-button"><Plus className="mr-2 h-4 w-4" />{t("scan.start")}</Button>
              )}
            </div>
          </div>

          {ambiguous && (
            <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/50 p-4" onClick={() => setAmbiguous(null)}>
              <Card className="w-full max-w-sm p-4" onClick={(e) => e.stopPropagation()} data-testid="scan-ambiguous">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="font-semibold">{t("scan.ambiguous")}</h3>
                  <button onClick={() => setAmbiguous(null)}><X className="h-4 w-4" /></button>
                </div>
                <div className="space-y-1">
                  {ambiguous.matches.map((m) => (
                    <button key={m.id} onClick={() => chooseAmbiguous(m.id)} data-testid="scan-ambiguous-option" className="flex w-full items-center justify-between rounded p-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800">
                      <span className="truncate">{m.name}</span><span className="text-xs text-slate-400">{m.sku}</span>
                    </button>
                  ))}
                </div>
              </Card>
            </div>
          )}
          <CameraBarcodeScanner open={camera} onOpenChange={setCamera} onDetected={submitScan} />
        </>
      )}
    </div>
  );
}
